import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router';
import { useDeletedPreview } from '@/hooks/use-deleted-preview';
import { getAssetOptions } from '@/queries/assets/get-asset';

interface AssetReadOnlyContextValue {
	// True when the current asset belongs to a deactivated organization, or when a deleted item is previewed:
	// everything under it is read-only
	isReadOnly: boolean;
	// True when the current asset itself is a deleted one, opened as a read-only preview (GH #331)
	isDeletedAsset: boolean;
	// True when the asset's organization is inactive, whether or not the asset is deleted: the reason for the
	// "organisation is inactive" banner, which a deleted asset of an active organization must not show
	isOrganizationInactive: boolean;
}

const AssetReadOnlyContext = createContext<AssetReadOnlyContextValue>({
	isReadOnly: false,
	isDeletedAsset: false,
	isOrganizationInactive: false,
});

/**
 * Marks the current asset (from the `:assetId` route param) read-only when its organization
 * is inactive, or when it is a deleted asset opened as a preview (`?deleted=asset`). Provided above both
 * panels in AssetMainOutlet; the permission hooks read it to drop write roles, so every
 * Restricted/usePermission-gated control turns read-only with it. The backend enforces the same rule;
 * this only keeps the UI from offering actions that would fail.
 */
export function AssetReadOnlyProvider({ children }: { children: React.ReactNode }) {
	const { assetId = '' } = useParams();
	const isDeletedAsset = useDeletedPreview('asset');
	const { data: asset } = useQuery(getAssetOptions(assetId, true, { deleted: isDeletedAsset }));
	const isOrganizationInactive = asset?.organization?.enabled === false;
	const isReadOnly = isDeletedAsset || isOrganizationInactive;

	const value = useMemo(
		() => ({ isReadOnly, isDeletedAsset, isOrganizationInactive }),
		[isReadOnly, isDeletedAsset, isOrganizationInactive],
	);

	return <AssetReadOnlyContext.Provider value={value}>{children}</AssetReadOnlyContext.Provider>;
}

/**
 * Makes one subtree read-only, e.g. a deleted inspection, damage or work package previewed under a live
 * asset (GH #331): the permission hooks below it drop every write role.
 */
export function ReadOnlySubtree({ active, children }: { active: boolean; children: React.ReactNode }) {
	const parent = useContext(AssetReadOnlyContext);
	const value = useMemo(() => (active ? { ...parent, isReadOnly: true } : parent), [active, parent]);
	return <AssetReadOnlyContext.Provider value={value}>{children}</AssetReadOnlyContext.Provider>;
}

export function useAssetReadOnly() {
	return useContext(AssetReadOnlyContext);
}
